import React, { useEffect, useMemo, useState } from 'react'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import { collection, doc, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore'
import { db } from '../firebase'

const emptySubmissionForm = {
  responseItems: [],
  responseText: '',
  githubLink: '',
  projectLink: '',
  demoVideoLink: '',
  notes: ''
}

const normalizeText = (value) => String(value || '').trim()

const formatFileSize = (value) => {
  const size = Number(value) || 0
  if (size <= 0) return ''
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`
  if (size >= 1024) return `${Math.round(size / 1024)} KB`
  return `${size} B`
}

const getAssignmentAttachment = (assignment = {}) => {
  const attachment = assignment?.assignmentAttachment
  if (!attachment || !normalizeText(attachment.url || attachment.downloadURL)) return null

  return {
    name: normalizeText(attachment.name) || 'Assignment file',
    url: normalizeText(attachment.url || attachment.downloadURL),
    size: Number(attachment.size) || 0,
    contentType: normalizeText(attachment.contentType || attachment.type)
  }
}

const normalizeCourseKey = (value) => normalizeText(value)
  .toLowerCase()
  .replace(/&/g, 'and')
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()

const normalizeTimestamp = (value) => {
  if (!value) return 0
  if (typeof value?.toMillis === 'function') return value.toMillis()
  if (typeof value?.seconds === 'number') return value.seconds * 1000
  return new Date(value).getTime() || 0
}

const formatCalendarDate = (value) => {
  const timestamp = normalizeTimestamp(value)
  if (!timestamp) return 'To be shared'

  return new Intl.DateTimeFormat('en-KE', {
    dateStyle: 'medium',
    timeStyle: typeof value === 'string' && value.length <= 10 ? undefined : 'short'
  }).format(new Date(timestamp))
}

const chunkArray = (items = [], size = 10) => {
  const result = []
  for (let index = 0; index < items.length; index += size) {
    result.push(items.slice(index, index + size))
  }
  return result
}

const assignmentUsesPromptItems = (assignmentType) => (
  assignmentType === 'questions_text' || assignmentType === 'questions_code'
)

const assignmentExpectsCodeResponse = (assignmentType) => (
  assignmentType === 'questions_code' || assignmentType === 'code_paste'
)

const assignmentAllowsGithubLink = (assignmentType) => (
  assignmentType === 'code' || assignmentType === 'code_link'
)

const assignmentAllowsProjectLink = (assignmentType) => (
  assignmentType === 'project' || assignmentType === 'project_demo'
)

const assignmentAllowsDemoVideo = (assignmentType) => (
  assignmentType === 'project' || assignmentType === 'project_demo'
)

const sanitizeRichTextHtml = (input) => {
  if (!input) return ''
  const parser = new DOMParser()
  const doc = parser.parseFromString(input, 'text/html')
  const allowedTags = new Set(['b', 'strong', 'i', 'em', 'u', 's', 'code', 'pre', 'br', 'p', 'div', 'ul', 'ol', 'li', 'blockquote', 'span'])

  const walk = (node) => {
    Array.from(node.childNodes).forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase()
        if (!allowedTags.has(tag)) {
          const fragment = doc.createDocumentFragment()
          while (child.firstChild) fragment.appendChild(child.firstChild)
          child.replaceWith(fragment)
          return
        }
        Array.from(child.attributes).forEach((attr) => child.removeAttribute(attr.name))
        walk(child)
      } else if (child.nodeType === Node.COMMENT_NODE) {
        child.remove()
      }
    })
  }

  walk(doc.body)
  return doc.body.innerHTML
}

const getAssignmentStatusLabel = (submission) => (
  submission?.reviewStatus || submission?.status || 'Pending'
)

const getAssignmentStatusKey = (submission) => {
  if (!submission) return 'pending'

  const status = normalizeCourseKey(submission.reviewStatus || submission.status || 'submitted')
  if (status.includes('revision') || status.includes('redo')) return 'revision'
  if (status.includes('review')) return 'reviewed'
  if (status.includes('resubmitted')) return 'resubmitted'
  return 'submitted'
}

const getAssignmentTypeLabel = (assignment) => {
  const type = assignment?.assignmentType
  if (assignment?.assignmentTypeLabel) return assignment.assignmentTypeLabel
  if (type === 'questions_text') return 'Questions to answer non code'
  if (type === 'questions_code') return 'Question to answer code submission'
  if (type === 'code_link' || type === 'code') return 'Code submission - GitHub link'
  if (type === 'code_paste') return 'Code submission - paste code'
  if (type === 'project_demo' || type === 'project') return 'Project link or demo'
  if (type === 'questions') return 'Questions to answer'
  if (type === 'general') return 'General task / reflection'
  return type || 'task'
}

const getSubmissionLinks = (submission) => (
  [
    {
      label: 'GitHub repository',
      value: submission?.githubLink || submission?.githubUrl || submission?.repositoryUrl || submission?.repoLink
    },
    {
      label: 'Project link',
      value: submission?.projectLink || submission?.projectUrl || submission?.liveLink || submission?.liveUrl
    },
    {
      label: 'Demo video',
      value: submission?.videoLink || submission?.videoUrl || submission?.demoVideoLink || submission?.demoUrl
    }
  ].filter((item) => normalizeText(item.value))
)

const getInitialSubmissionForm = (assignment, submission) => {
  const promptItems = Array.isArray(assignment?.promptItems) ? assignment.promptItems : []
  const savedResponseItems = Array.isArray(submission?.responseItems)
    ? submission.responseItems.map((item) => normalizeText(item))
    : []

  const responseItems = promptItems.map((_, index) => savedResponseItems[index] || '')

  return {
    responseItems,
    responseText: submission?.responseText || submission?.answerText || '',
    githubLink: submission?.githubLink || submission?.githubUrl || submission?.repositoryUrl || submission?.repoLink || '',
    projectLink: submission?.projectLink || submission?.projectUrl || submission?.liveLink || submission?.liveUrl || '',
    demoVideoLink: submission?.demoVideoLink || submission?.demoUrl || submission?.videoLink || submission?.videoUrl || '',
    notes: submission?.notes || submission?.comment || ''
  }
}

const getSubmissionPrimaryLabel = (assignmentType) => {
  if (assignmentType === 'questions' || assignmentType === 'questions_text') return 'Answers'
  if (assignmentType === 'questions_code' || assignmentType === 'code_paste') return 'Paste code'
  if (assignmentType === 'project' || assignmentType === 'project_demo') return 'Project summary'
  return 'Work summary'
}

const getSubmissionPrimaryPlaceholder = (assignmentType) => {
  if (assignmentType === 'questions' || assignmentType === 'questions_text') {
    return 'Write your answers, explanations, or reflections here.'
  }
  if (assignmentType === 'questions_code' || assignmentType === 'code_paste') {
    return 'Paste your code response here.'
  }
  if (assignmentType === 'project' || assignmentType === 'project_demo') {
    return 'Describe what you built, what works, and what still needs improvement.'
  }
  return 'Explain your solution, what you built, and anything your instructor should review.'
}

const getAssignmentDisplayHtml = (assignment = {}) => {
  const instructionsHtml = sanitizeRichTextHtml(assignment.instructionsHtml || assignment.instructions || '')
  if (instructionsHtml) return instructionsHtml

  if (Array.isArray(assignment.promptItems) && assignment.promptItems.length > 0) {
    return assignment.promptItems
      .map((prompt, index) => (
        `<div class="studentAssignmentPromptDisplay"><span>Question ${index + 1}</span>${sanitizeRichTextHtml(prompt)}</div>`
      ))
      .join('')
  }

  return '<p>No instructions were added for this assignment yet.</p>'
}

const buildSubmissionDocId = (assignmentId, studentId) => (
  `${String(assignmentId || '').replace(/\//g, '__')}__${String(studentId || '').replace(/\//g, '__')}`
)

const buildAssignmentReadDocId = (studentId, assignmentId) => (
  `${String(studentId || '').replace(/\//g, '__')}__${String(assignmentId || '').replace(/\//g, '__')}`
)

function AssTasksComp() {
  const auth = getAuth()
  const [loading, setLoading] = useState(true)
  const [loadMessage, setLoadMessage] = useState('')
  const [studentProfile, setStudentProfile] = useState(null)
  const [userId, setUserId] = useState('')
  const [assignments, setAssignments] = useState([])
  const [assignmentSubmissions, setAssignmentSubmissions] = useState({})
  const [selectedAssignmentId, setSelectedAssignmentId] = useState('')
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [showSubmittedAssignmentEditor, setShowSubmittedAssignmentEditor] = useState(false)
  const [submissionForm, setSubmissionForm] = useState(emptySubmissionForm)
  const [submissionNotice, setSubmissionNotice] = useState('')
  const [savingSubmission, setSavingSubmission] = useState(false)

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setLoading(false)
        setLoadMessage('Please sign in to view your assignments.')
        return
      }

      setLoading(true)
      setLoadMessage('')
      setUserId(user.uid)

      try {
        const profileSnapshot = await getDocs(query(collection(db, 'users'), where('userId', '==', user.uid)))
        const profileData = profileSnapshot.docs[0]?.data() || {}
        const resolvedProfile = {
          id: profileSnapshot.docs[0]?.id || user.uid,
          userId: profileData.userId || user.uid,
          name: profileData.bio || profileData.name || user.displayName || 'Student',
          email: profileData.userEmail || profileData.email || user.email || '',
          course: profileData.course || profileData.program || profileData.courseName || ''
        }
        setStudentProfile(resolvedProfile)

        const courseKeys = new Set(
          [
            resolvedProfile.course,
            profileData.currentCourse,
            profileData.program,
            profileData.courseName
          ]
            .map(normalizeCourseKey)
            .filter(Boolean)
        )

        const assignmentsSnapshot = await getDocs(collection(db, 'courseAssignments'))
        const studentAssignments = assignmentsSnapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((assignment) => {
            const assignmentStudentIds = Array.isArray(assignment.studentIds)
              ? assignment.studentIds.map((studentId) => String(studentId))
              : []
            const assignmentCourseKey = normalizeCourseKey(assignment.courseKey || assignment.courseName || assignment.courseId)
            const matchesStudent = (
              normalizeCourseKey(assignment.audience) === 'all' ||
              assignmentStudentIds.length === 0 ||
              assignmentStudentIds.includes(user.uid)
            )
            const matchesCourse = courseKeys.size === 0 || courseKeys.has(assignmentCourseKey)

            return matchesStudent && (matchesCourse || assignmentStudentIds.includes(user.uid))
          })
          .sort((first, second) => {
            const firstDue = normalizeTimestamp(first.dueDate)
            const secondDue = normalizeTimestamp(second.dueDate)

            if (firstDue && secondDue) return firstDue - secondDue
            if (firstDue) return -1
            if (secondDue) return 1

            return normalizeTimestamp(second.createdAt) - normalizeTimestamp(first.createdAt)
          })

        setAssignments(studentAssignments)

        if (studentAssignments.length > 0) {
          const submissionsByAssignment = {}
          const assignmentChunks = chunkArray(studentAssignments.map((assignment) => assignment.id).filter(Boolean), 10)

          await Promise.all(assignmentChunks.map(async (assignmentChunk) => {
            const submissionSnapshot = await getDocs(
              query(collection(db, 'assignmentSubmissions'), where('assignmentId', 'in', assignmentChunk))
            )

            submissionSnapshot.forEach((docSnap) => {
              const submission = { id: docSnap.id, ...docSnap.data() }
              const submissionStudentId = submission.studentId || submission.studentID || submission.userId || submission.submittedById
              const submissionEmail = normalizeText(submission.studentEmail || submission.userEmail).toLowerCase()

              if (submissionStudentId !== user.uid && submissionEmail !== normalizeText(user.email).toLowerCase()) return

              const previousSubmission = submissionsByAssignment[submission.assignmentId]
              const previousTimestamp = normalizeTimestamp(previousSubmission?.updatedAt || previousSubmission?.submittedAt || previousSubmission?.createdAt)
              const nextTimestamp = normalizeTimestamp(submission.updatedAt || submission.submittedAt || submission.createdAt)

              if (!previousSubmission || nextTimestamp >= previousTimestamp) {
                submissionsByAssignment[submission.assignmentId] = submission
              }
            })
          }))

          setAssignmentSubmissions(submissionsByAssignment)
        } else {
          setAssignmentSubmissions({})
        }

        if (!studentAssignments.length) {
          setLoadMessage('No assignments have been issued to your account yet.')
        }
      } catch (error) {
        console.error('Error loading assignments:', error)
        setLoadMessage('We could not load your assignments right now. Please try again shortly.')
      } finally {
        setLoading(false)
      }
    })

    return () => unsubscribe()
  }, [auth])

  useEffect(() => {
    if (!assignments.length) {
      setSelectedAssignmentId('')
      setIsDetailOpen(false)
      return
    }

    if (!assignments.some((assignment) => assignment.id === selectedAssignmentId)) {
      setSelectedAssignmentId(assignments[0].id)
    }
  }, [assignments, selectedAssignmentId])

  const assignmentStats = useMemo(() => {
    const values = assignments.reduce((accumulator, assignment) => {
      const statusKey = getAssignmentStatusKey(assignmentSubmissions[assignment.id])
      accumulator.total += 1
      if (statusKey === 'pending') accumulator.pending += 1
      if (statusKey === 'submitted' || statusKey === 'resubmitted') accumulator.submitted += 1
      if (statusKey === 'reviewed') accumulator.reviewed += 1
      if (statusKey === 'revision') accumulator.revision += 1
      return accumulator
    }, {
      total: 0,
      pending: 0,
      submitted: 0,
      reviewed: 0,
      revision: 0
    })

    return values
  }, [assignmentSubmissions, assignments])

  const selectedAssignment = useMemo(
    () => assignments.find((assignment) => assignment.id === selectedAssignmentId) || null,
    [assignments, selectedAssignmentId]
  )

  const selectedSubmission = useMemo(
    () => (selectedAssignment ? assignmentSubmissions[selectedAssignment.id] || null : null),
    [assignmentSubmissions, selectedAssignment]
  )

  useEffect(() => {
    if (!selectedAssignment) {
      setSubmissionForm(emptySubmissionForm)
      return
    }

    setSubmissionForm(getInitialSubmissionForm(selectedAssignment, assignmentSubmissions[selectedAssignment.id]))
  }, [assignmentSubmissions, selectedAssignment])

  useEffect(() => {
    setShowSubmittedAssignmentEditor(!selectedSubmission)
  }, [selectedAssignmentId, selectedSubmission])

  useEffect(() => {
    if (!userId || !selectedAssignment?.id || !isDetailOpen) return

    setDoc(doc(db, 'studentAssignmentReads', buildAssignmentReadDocId(userId, selectedAssignment.id)), {
      studentId: userId,
      assignmentId: selectedAssignment.id,
      courseName: selectedAssignment.courseName || studentProfile?.course || '',
      readAt: serverTimestamp(),
      readAtMs: Date.now()
    }, { merge: true }).catch((error) => {
      console.error('Error marking student assignment as read:', error)
    })
  }, [isDetailOpen, selectedAssignment?.courseName, selectedAssignment?.id, studentProfile?.course, userId])

  const openAssignment = (assignmentId) => {
    setSelectedAssignmentId(assignmentId)
    setSubmissionNotice('')
    setIsDetailOpen(true)
  }

  const updateSubmissionField = (field, value) => {
    setSubmissionForm((current) => ({ ...current, [field]: value }))
  }

  const updateSubmissionResponseItem = (index, value) => {
    setSubmissionForm((current) => ({
      ...current,
      responseItems: (current.responseItems || []).map((item, itemIndex) => (
        itemIndex === index ? value : item
      ))
    }))
  }

  const submitAssignment = async (event) => {
    event.preventDefault()

    if (!selectedAssignment || !userId) return

    const normalizedResponseItems = Array.isArray(submissionForm.responseItems)
      ? submissionForm.responseItems.map((value) => normalizeText(value))
      : []

    const hasPromptResponse = normalizedResponseItems.some(Boolean)
    const hasDirectResponse = normalizeText(submissionForm.responseText)
    const hasLinks = (
      normalizeText(submissionForm.githubLink) ||
      normalizeText(submissionForm.projectLink) ||
      normalizeText(submissionForm.demoVideoLink)
    )
    const hasNotes = normalizeText(submissionForm.notes)

    if (!hasPromptResponse && !hasDirectResponse && !hasLinks && !hasNotes) {
      setSubmissionNotice('Add your work or a link before submitting this assignment.')
      return
    }

    setSavingSubmission(true)
    setSubmissionNotice('')

    try {
      const existingSubmission = assignmentSubmissions[selectedAssignment.id]
      const submissionDocId = existingSubmission?.id || buildSubmissionDocId(selectedAssignment.id, userId)
      const submissionState = existingSubmission ? 'resubmitted' : 'submitted'
      const now = new Date()

      const payload = {
        assignmentId: selectedAssignment.id,
        assignmentTitle: selectedAssignment.title || 'Assignment',
        courseName: selectedAssignment.courseName || selectedAssignment.courseKey || '',
        courseId: selectedAssignment.courseId || '',
        assignmentType: selectedAssignment.assignmentType || '',
        assignmentTypeLabel: getAssignmentTypeLabel(selectedAssignment),
        promptItems: Array.isArray(selectedAssignment.promptItems) ? selectedAssignment.promptItems : [],
        studentId: userId,
        studentID: userId,
        userId,
        studentName: studentProfile?.name || auth.currentUser?.displayName || 'Student',
        studentEmail: studentProfile?.email || auth.currentUser?.email || '',
        responseItems: normalizedResponseItems,
        responseText: normalizeText(submissionForm.responseText),
        answerText: normalizeText(submissionForm.responseText),
        githubLink: normalizeText(submissionForm.githubLink),
        githubUrl: normalizeText(submissionForm.githubLink),
        repositoryUrl: normalizeText(submissionForm.githubLink),
        projectLink: normalizeText(submissionForm.projectLink),
        projectUrl: normalizeText(submissionForm.projectLink),
        liveLink: normalizeText(submissionForm.projectLink),
        demoVideoLink: normalizeText(submissionForm.demoVideoLink),
        videoLink: normalizeText(submissionForm.demoVideoLink),
        videoUrl: normalizeText(submissionForm.demoVideoLink),
        notes: normalizeText(submissionForm.notes),
        comment: normalizeText(submissionForm.notes),
        status: submissionState,
        reviewStatus: submissionState,
        updatedAt: serverTimestamp(),
        updatedAtMs: now.getTime()
      }

      if (!existingSubmission) {
        payload.createdAt = serverTimestamp()
        payload.createdAtMs = now.getTime()
        payload.submittedAt = serverTimestamp()
        payload.submittedAtMs = now.getTime()
      } else {
        payload.createdAt = existingSubmission.createdAt || serverTimestamp()
        payload.createdAtMs = existingSubmission.createdAtMs || normalizeTimestamp(existingSubmission.createdAt) || now.getTime()
        payload.submittedAt = existingSubmission.submittedAt || serverTimestamp()
        payload.submittedAtMs = existingSubmission.submittedAtMs || normalizeTimestamp(existingSubmission.submittedAt) || now.getTime()
      }

      await setDoc(doc(db, 'assignmentSubmissions', submissionDocId), payload, { merge: true })

      const localPayload = {
        ...payload,
        id: submissionDocId,
        createdAt: existingSubmission?.createdAt || now.toISOString(),
        submittedAt: existingSubmission?.submittedAt || now.toISOString(),
        updatedAt: now.toISOString()
      }

      setAssignmentSubmissions((current) => ({
        ...current,
        [selectedAssignment.id]: localPayload
      }))
      setSubmissionNotice('')
      setShowSubmittedAssignmentEditor(false)
      setIsDetailOpen(false)
    } catch (error) {
      console.error('Error submitting assignment:', error)
      setSubmissionNotice('We could not save your assignment right now. Please try again.')
    } finally {
      setSavingSubmission(false)
    }
  }

  const renderSubmittedResponse = (label, value, isCode = false) => {
    if (!normalizeText(value)) return null

    return (
      <div className='studentAssignmentSubmissionAnswer' key={`${label}-${value.slice(0, 20)}`}>
        <span>{label}</span>
        {isCode ? <pre>{value}</pre> : <p>{value}</p>}
      </div>
    )
  }

  if (loading) {
    return (
      <div className='studentAssignmentsPage'>
        <div className='assignmentStateCard'>
          <div className='spinner-border text-dark' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
          <p>Loading your assignments...</p>
        </div>
      </div>
    )
  }

  return (
    <div className='studentAssignmentsPage'>
      <section className='studentAssignmentsHero'>
        <span>Assignments</span>
        <h3>My Assignments</h3>
        <p>Open any assignment to read the full brief, review what you already submitted, and update it when needed.</p>
      </section>

      <section className='studentAssignmentsStats'>
        <div>
          <span>Total assignments</span>
          <strong>{assignmentStats.total}</strong>
        </div>
        <div>
          <span>Open</span>
          <strong>{assignmentStats.pending}</strong>
        </div>
        <div>
          <span>Submitted</span>
          <strong>{assignmentStats.submitted}</strong>
        </div>
        <div>
          <span>Reviewed</span>
          <strong>{assignmentStats.reviewed}</strong>
        </div>
      </section>

      {assignments.length > 0 ? (
        !isDetailOpen ? (
          <section className='studentAssignmentLauncher'>
            {assignments.map((assignment) => {
              const submission = assignmentSubmissions[assignment.id]
              const statusKey = getAssignmentStatusKey(submission)

              return (
                <button
                  type='button'
                  className='studentAssignmentCard studentAssignmentLauncherCard'
                  key={assignment.id}
                  onClick={() => openAssignment(assignment.id)}
                >
                  <div className='studentAssignmentCardTop'>
                    <div>
                      <span className='studentAssignmentEyebrow'>{getAssignmentTypeLabel(assignment)}</span>
                      <h4>{assignment.title || 'Untitled assignment'}</h4>
                    </div>
                    <span className={`studentAssignmentStatus studentAssignmentStatus-${statusKey}`}>
                      {getAssignmentStatusLabel(submission)}
                    </span>
                  </div>

                  <div className='studentAssignmentMeta'>
                    <span>{assignment.courseName || studentProfile?.course || 'Course assignment'}</span>
                    <span>Due {assignment.dueDate ? formatCalendarDate(assignment.dueDate) : 'To be shared'}</span>
                    <span>{submission ? `Last updated ${formatCalendarDate(submission.updatedAt || submission.submittedAt || submission.createdAt)}` : 'Not submitted yet'}</span>
                  </div>

                  <div className='studentAssignmentOpenHint'>Open assignment</div>
                </button>
              )
            })}
          </section>
        ) : (
          <section className='studentAssignmentFullscreen'>
            <div className='studentAssignmentFullscreenBar'>
              <button
                type='button'
                className='studentAssignmentBackButton'
                onClick={() => {
                  setIsDetailOpen(false)
                  setSubmissionNotice('')
                }}
              >
                Back to assignments
              </button>
            </div>

            {selectedAssignment ? (
              <div className='studentAssignmentEditor studentAssignmentEditorFull'>
                <div className='studentAssignmentEditorTop'>
                  <div>
                    <span className='studentAssignmentEyebrow'>Assignment workspace</span>
                    <h4>{selectedAssignment.title || 'Assignment'}</h4>
                    <div
                      className='studentAssignmentRichText'
                      dangerouslySetInnerHTML={{ __html: getAssignmentDisplayHtml(selectedAssignment) }}
                    />
                  </div>
                  <span className={`studentAssignmentStatus studentAssignmentStatus-${getAssignmentStatusKey(selectedSubmission)}`}>
                    {getAssignmentStatusLabel(selectedSubmission)}
                  </span>
                </div>

                <div className='studentAssignmentInfoGrid'>
                  <div>
                    <span>Course</span>
                    <strong>{selectedAssignment.courseName || studentProfile?.course || 'Course assignment'}</strong>
                  </div>
                  <div>
                    <span>Due date</span>
                    <strong>{selectedAssignment.dueDate ? formatCalendarDate(selectedAssignment.dueDate) : 'No due date'}</strong>
                  </div>
                  <div>
                    <span>Last update</span>
                    <strong>{selectedSubmission ? formatCalendarDate(selectedSubmission.updatedAt || selectedSubmission.submittedAt || selectedSubmission.createdAt) : 'Not submitted yet'}</strong>
                  </div>
                </div>

                {selectedAssignment.resourceLink && (
                  <a
                    href={selectedAssignment.resourceLink}
                    target='_blank'
                    rel='noreferrer'
                    className='studentAssignmentResource'
                  >
                    Open assignment resource
                  </a>
                )}

                {getAssignmentAttachment(selectedAssignment) && (
                  <div className='studentAssignmentSubmissionLinksBlock'>
                    <span className='studentAssignmentEyebrow'>Assignment file</span>
                    <div className='studentAssignmentLinks'>
                      <a
                        href={getAssignmentAttachment(selectedAssignment).url}
                        target='_blank'
                        rel='noreferrer'
                        download={getAssignmentAttachment(selectedAssignment).name}
                        className='studentAssignmentLinkCard'
                      >
                        <span>Download file</span>
                        <strong>
                          {getAssignmentAttachment(selectedAssignment).name}
                          {formatFileSize(getAssignmentAttachment(selectedAssignment).size)
                            ? ` • ${formatFileSize(getAssignmentAttachment(selectedAssignment).size)}`
                            : ''}
                        </strong>
                      </a>
                    </div>
                  </div>
                )}

                {selectedSubmission && (
                  <div className='studentAssignmentSubmittedBlock'>
                    <div className='studentAssignmentSectionTop'>
                      <div>
                        <span className='studentAssignmentEyebrow'>Your submission</span>
                        <h5>Submitted work</h5>
                      </div>
                      <span className={`studentAssignmentStatus studentAssignmentStatus-${getAssignmentStatusKey(selectedSubmission)}`}>
                        {getAssignmentStatusLabel(selectedSubmission)}
                      </span>
                    </div>

                    <div className='studentAssignmentSubmissionList'>
                      {Array.isArray(selectedSubmission.responseItems) && selectedSubmission.responseItems.map((item, index) => (
                        renderSubmittedResponse(`Question ${index + 1}`, item, assignmentExpectsCodeResponse(selectedAssignment.assignmentType))
                      ))}

                      {renderSubmittedResponse(
                        assignmentExpectsCodeResponse(selectedAssignment.assignmentType) ? 'Submitted code' : 'Submitted response',
                        selectedSubmission.responseText || selectedSubmission.answerText,
                        assignmentExpectsCodeResponse(selectedAssignment.assignmentType)
                      )}

                      {renderSubmittedResponse('Note to instructor', selectedSubmission.notes || selectedSubmission.comment)}
                    </div>

                    {getSubmissionLinks(selectedSubmission).length > 0 && (
                      <div className='studentAssignmentSubmissionLinksBlock'>
                        <span className='studentAssignmentEyebrow'>Shared links</span>
                        <div className='studentAssignmentLinks'>
                          {getSubmissionLinks(selectedSubmission).map((link) => (
                            <a
                              key={`${selectedAssignment.id}-${link.label}`}
                              className='studentAssignmentLinkCard'
                              href={link.value}
                              target='_blank'
                              rel='noreferrer'
                            >
                              <span>{link.label}</span>
                              <strong>{link.value}</strong>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {selectedSubmission && !showSubmittedAssignmentEditor && (
                  <div className='studentAssignmentInlineActions'>
                    <button
                      type='button'
                      className='studentAssignmentSecondaryButton'
                      onClick={() => {
                        setShowSubmittedAssignmentEditor(true)
                        setSubmissionNotice('')
                      }}
                    >
                      Update submission
                    </button>
                  </div>
                )}

                {(!selectedSubmission || showSubmittedAssignmentEditor) && (selectedAssignment.promptItems?.length > 0) && (
                  <div className='studentAssignmentQuestionSet'>
                    {selectedAssignment.promptItems.map((prompt, index) => (
                      <label className='studentAssignmentField' key={`${selectedAssignment.id}-prompt-${index}`}>
                        <span>Question {index + 1}</span>
                        <div
                          className='studentAssignmentPromptText'
                          dangerouslySetInnerHTML={{ __html: sanitizeRichTextHtml(prompt) }}
                        />
                        <textarea
                          value={submissionForm.responseItems?.[index] || ''}
                          onChange={(event) => updateSubmissionResponseItem(index, event.target.value)}
                          placeholder={
                            assignmentExpectsCodeResponse(selectedAssignment.assignmentType)
                              ? 'Paste your code answer for this question here.'
                              : 'Write your answer for this question here.'
                          }
                          rows={assignmentExpectsCodeResponse(selectedAssignment.assignmentType) ? 8 : 5}
                        />
                      </label>
                    ))}
                  </div>
                )}

                {(!selectedSubmission || showSubmittedAssignmentEditor) && (
                  <form className='studentAssignmentForm' onSubmit={submitAssignment}>
                  {!assignmentUsesPromptItems(selectedAssignment.assignmentType) && (
                    <label className='studentAssignmentField'>
                      <span>{getSubmissionPrimaryLabel(selectedAssignment.assignmentType)}</span>
                      <textarea
                        value={submissionForm.responseText}
                        onChange={(event) => updateSubmissionField('responseText', event.target.value)}
                        placeholder={getSubmissionPrimaryPlaceholder(selectedAssignment.assignmentType)}
                        rows={assignmentExpectsCodeResponse(selectedAssignment.assignmentType) ? 10 : 7}
                      />
                    </label>
                  )}

                  <div className='studentAssignmentFieldGrid'>
                    {assignmentAllowsGithubLink(selectedAssignment.assignmentType) && (
                      <label className='studentAssignmentField'>
                        <span>GitHub repository</span>
                        <input
                          type='url'
                          value={submissionForm.githubLink}
                          onChange={(event) => updateSubmissionField('githubLink', event.target.value)}
                          placeholder='https://github.com/your-project'
                        />
                      </label>
                    )}

                    {assignmentAllowsProjectLink(selectedAssignment.assignmentType) && (
                      <label className='studentAssignmentField'>
                        <span>Project or live link</span>
                        <input
                          type='url'
                          value={submissionForm.projectLink}
                          onChange={(event) => updateSubmissionField('projectLink', event.target.value)}
                          placeholder='https://your-project-link.com'
                        />
                      </label>
                    )}

                    {assignmentAllowsDemoVideo(selectedAssignment.assignmentType) && (
                      <label className='studentAssignmentField'>
                        <span>Demo video link</span>
                        <input
                          type='url'
                          value={submissionForm.demoVideoLink}
                          onChange={(event) => updateSubmissionField('demoVideoLink', event.target.value)}
                          placeholder='https://loom.com/...'
                        />
                      </label>
                    )}
                  </div>

                  <label className='studentAssignmentField'>
                    <span>Note to instructor</span>
                    <textarea
                      value={submissionForm.notes}
                      onChange={(event) => updateSubmissionField('notes', event.target.value)}
                      placeholder='Add context, blockers, or anything your instructor should know.'
                      rows={4}
                    />
                  </label>

                  {submissionNotice && (
                    <div className='studentAssignmentNotice'>{submissionNotice}</div>
                  )}

                  <div className='studentAssignmentFormActions'>
                    {selectedSubmission && (
                      <button
                        type='button'
                        className='studentAssignmentSecondaryButton'
                        onClick={() => {
                          setShowSubmittedAssignmentEditor(false)
                          setSubmissionNotice('')
                          setSubmissionForm(getInitialSubmissionForm(selectedAssignment, selectedSubmission))
                        }}
                      >
                        Cancel
                      </button>
                    )}
                    <button type='submit' className='studentAssignmentSubmit' disabled={savingSubmission}>
                      {savingSubmission
                        ? 'Saving...'
                        : selectedSubmission
                          ? 'Update submission'
                        : 'Submit assignment'}
                    </button>
                  </div>
                  </form>
                )}
              </div>
            ) : (
              <div className='assignmentStateCard'>
                <h5>No assignment selected</h5>
                <p>Return to the list and choose an assignment to continue.</p>
              </div>
            )}
          </section>
        )
      ) : (
        <div className='assignmentStateCard'>
          <h5>No assignments yet</h5>
          <p>{loadMessage || 'Your instructor has not issued any assignments to you yet.'}</p>
        </div>
      )}
    </div>
  )
}

export default AssTasksComp
